/**
 * Split an author into the name and the nickname, whatever shape they arrive in.
 *
 * Three different payloads reach the feed for the same person:
 *
 *  - the author-payload service, with `full_name` and `nickname` separately;
 *  - the raw user model, with `name` and `surname` and no nickname at all;
 *  - and `subscriber_full_name`, which is a LABEL, not a field —
 *    MasterClubSubscriber::getFullNameWithNicknameAttribute() returns
 *    "Nome Cognome (nickname)" when both exist, and the bare nickname when the
 *    account has no name.
 *
 * That last one was read as if it were the nickname on its own. Wrapping it a
 * second time produced the doubled label members reported in the comments —
 * "Anna V. (@Anna V. (ann…" — and the same nesting under a post author, where
 * the second line repeated the whole name instead of showing just the nickname.
 *
 * @returns {{fullName: string, nickname: string}} nickname is "" when there is
 *          nothing worth showing beside the name.
 */
export const resolveAuthorIdentity = (author) => {
  const fullName =
    (author?.full_name || "").trim() ||
    [author?.name, author?.surname].filter(Boolean).join(" ").trim();

  const explicitNickname = (author?.nickname || "").trim();
  const composed = (author?.subscriber_full_name || "").trim();

  // Given to us properly: nothing to unpick.
  if (explicitNickname) {
    return { fullName: fullName || composed, nickname: explicitNickname };
  }

  if (!composed) return { fullName, nickname: "" };

  // No real name on the account — the label IS the nickname, and it is all we
  // have to show.
  if (!fullName) return { fullName: composed, nickname: "" };

  // "Nome Cognome (nickname)" — take the nickname back out of it.
  const parenthesised = composed.match(/^(.*?)\s*\((.+)\)$/);
  if (
    parenthesised &&
    parenthesised[1].trim().toLowerCase() === fullName.toLowerCase()
  ) {
    return { fullName, nickname: parenthesised[2].trim() };
  }

  // Anything else: only worth showing if it says something the name does not.
  return {
    fullName,
    nickname: composed.toLowerCase() === fullName.toLowerCase() ? "" : composed,
  };
};

/** "Nome Cognome (@nickname)", or just the name when there is no nickname. */
export const authorLabelWithNickname = (author) => {
  const { fullName, nickname } = resolveAuthorIdentity(author);

  if (!fullName) return nickname;
  if (!nickname || nickname.toLowerCase() === fullName.toLowerCase()) {
    return fullName;
  }

  return `${fullName} (@${nickname})`;
};
